import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import { RouteComponentProps } from 'react-router';
import * as Utils from './Utils';

interface PaymentItem {
    id: number;
    name: string;
    amount: number;
    amountFormatted: string;
    selected: boolean;
}

interface INameToValueMap {
    [key: string]: any;
}

const JobPaymentData = (props: RouteComponentProps<{}>) => {
    const [loading, setLoading] = React.useState(true);
    const [jobDetails, setJobDetails] = React.useState({ jobId: 0, siteAddress: "" });
    const [paymentItems, setPaymentItems] = React.useState<PaymentItem[]>([]);
    const [paymentTotal, setPaymentTotal] = React.useState({ totalAmount: 0, totalAmountFormatted: "$0.00" });
    const [creditCard, setCreditCard] = React.useState({ cardNumber: "", cardName: "", cardExpiry: "", cardCode: "" });
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});

    React.useEffect(() => {
        window.scrollTo(0, 0);

        let jobParams: INameToValueMap = {};
        jobParams["id"] = 0;
        jobParams = props.match.params;
        let Id = jobParams["id"];

        getData(Id);
    }, []);

    const getData = async (jobId: number) => {
        const token = await authService.getAccessToken();

        axios.get('api/Job/GetJobPaymentItems?JobId=' + jobId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
            .then(res => {
                setJobDetails({ jobId: jobId, siteAddress: res.data.siteAddress });
                setPaymentItems(res.data.jobPaymentItems);
                setPaymentTotal({ totalAmount: res.data.totalAmount, totalAmountFormatted: res.data.totalAmountFormatted });
                setLoading(false);
            }).catch(error => {
                toast.error(error.message);
            });
    }

    const handleCheckboxChange = (e: any, index: number) => {
        deleteError("items");
        var payItems = [...paymentItems];
        var payItem = payItems[index];
        payItem.selected = e.target.checked;
        setPaymentItems(payItems);

        //update total
        var total = 0;
        for (var i = 0; i < payItems.length; i++) {
            if (payItems[i].selected) {
                total += payItems[i].amount;
            }
        }
        var totalCurr = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
        setPaymentTotal(prevState => ({ ...prevState, totalAmount: total, totalAmountFormatted: totalCurr }));
    }

    const deleteError = (errorName: string) => {
        var error = errors;
        delete error[errorName];
        setErrors(error);
    }

    const handleCardNameChange = (e: any) => {
        if (e.target.value) {
            deleteError(e.target.name);
        }
        setCreditCard(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    const handleCardNumberChange = (e: any) => {
        if (e.target.value) {
            deleteError(e.target.name);
        }
        var reg = /^[0-9\s\-]+$/;
        handleChange(e, reg);
    }

    const handleCardExpiryChange = (e: any) => {
        if (e.target.value) {
            deleteError(e.target.name);
        }
        var reg = /^[0-9\//]+$/;
        handleChange(e, reg);
    }

    const handleCardCodeChange = (e: any) => {
        if (e.target.value) {
            deleteError(e.target.name);
        }
        var reg = /^[0-9]+$/;
        handleChange(e, reg);
    }

    const handleChange = (e: any, reg: RegExp) => {
        if (e.target.value.length === 0 || reg.test(e.target.value)) {
            setCreditCard(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
        }
    }

    const validate = () => {
        var formIsValid = true;
        let error: any = {};

        //check payment total > 0
        var total = 0;
        for (var i = 0; i < paymentItems.length; i++) {
            if (paymentItems[i].selected) {
                total += paymentItems[i].amount;
            }
        }
        if (total <= 0) {
            formIsValid = false;
            error["items"] = "Select at least one item for payment!";
        }

        //check card number
        var cardNo = creditCard.cardNumber.replaceAll(" ", "").replaceAll("-", "");
        if (cardNo.length < 8 || cardNo.length > 19) {
            formIsValid = false;
            error["cardNumber"] = "Card Number must be between 8 and 19 characters!";
        } else if (!Utils.isNumeric(cardNo)) {
            formIsValid = false;
            error["cardNumber"] = "Card Number must ONLY contain numbers!";
        }

        var cardName = creditCard.cardName;
        if (!cardName || cardName === "") {
            formIsValid = false;
            error["cardName"] = "Card Name is required!";
        }

        var currentDate = new Date();
        var currentFullYear = currentDate.getFullYear().toString();
        var currentYear = parseInt(currentFullYear.substring(2, 4));
        var maxYear = currentYear + 6;

        var cardExpiry = creditCard.cardExpiry.replace("/", "");
        if (cardExpiry.length != 4) {
            formIsValid = false;
            error["cardExpiry"] = "Card Expiry must be 4 digits - DD/MM";
        } else if (!Utils.isNumeric(cardExpiry)) {
            formIsValid = false;
            error["cardExpiry"] = "Card Expiry must ONLY contain numbers!";
        } else if (parseInt(cardExpiry.substring(0, 2)) > 12) {
            formIsValid = false;
            error["cardExpiry"] = "Card Expiry Month (MM) must be between 1 and 12!";
        } else if (parseInt(cardExpiry.substring(2, 4)) < currentYear || parseInt(cardExpiry.substring(2, 2)) > maxYear) {
            formIsValid = false;
            error["cardExpiry"] = "Card Expiry Year (YY) must be within the next 5 years!";
        }

        var csv = creditCard.cardCode;
        if (csv.length < 3 || csv.length > 4) {
            formIsValid = false;
            error["cardCode"] = "Card CSV must be 3 or 4 digits";
        } else if (!Utils.isNumeric(csv)) {
            formIsValid = false;
            error["cardCode"] = "Card CSV must ONLY contain numbers!";
        }
        
        setErrors(error);

        return formIsValid;
    }

    const chargeCreditCard = () => {
        if (validate()) {
            toast.info("Card charged");
        }
    }

    const renderDetails = (
        <div>
            <h1>{jobDetails.jobId} - {jobDetails.siteAddress}</h1>
            <hr></hr>
            <div className="overflowAuto">
                <h4>Items Requiring Payment</h4>
                {paymentItems.map((item, index) =>
                    <div key={item.id} className="jobpayment--items">
                        <div>
                            <input type="checkbox" className="marginRight10" name="selected" id="selected" checked={item.selected} onChange={(e) => handleCheckboxChange(e, index)}></input>
                            <span>{item.name}</span>
                        </div>
                        <span>{item.amountFormatted}</span>
                    </div>
                )}

                <div className="jobpayment--items makeitbold">
                    <span>Payment Total</span>
                    <span className="jobpayment--total">{paymentTotal.totalAmountFormatted}</span>
                </div>
                <span className="label errors">{errors["items"]}</span>
            </div>
            <hr></hr>
            <div className="jobpayment--screen-size">
                <h1>Credit Card Payment</h1>
                <label className="input-group--nowrap" htmlFor="cardNumber">
                    <span className="label">Card Number</span>
                    <input className="input jobpayment--input-size" maxLength={19} type="text" pattern="[0-9]" placeholder="XXXX XXXX XXXX XXXX" id="cardNumber" name="cardNumber" value={creditCard.cardNumber} onChange={(e) => handleCardNumberChange(e)}></input>
                </label>
                <span className="label errors">{errors["cardNumber"]}</span>

                <label className="input-group--nowrap" htmlFor="cardName">
                    <span className="label">Card Name</span>
                    <input className="input jobpayment--input-size" type="text" id="cardName" name="cardName" value={creditCard.cardName} onChange={(e) => handleCardNameChange(e)}></input>
                </label>
                <span className="label errors">{errors["cardName"]}</span>

                <div className="jobpayment--input-size makeitflexspacebetween">
                    <div>
                        <label className="input-group--nowrap" htmlFor="cardExpiry">
                            <span className="label">Card Expiry</span>
                            <input className="input jobpayment--input-split-size" type="text" pattern="[0-9/]+" maxLength={5} placeholder="MM/YY" id="cardExpiry" name="cardExpiry" value={creditCard.cardExpiry} onChange={(e) => handleCardExpiryChange(e)}></input>
                        </label>
                        <span className="label errors">{errors["cardExpiry"]}</span>
                    </div>
                    <div>
                        <label className="input-group--nowrap marginLeft20" htmlFor="cardCode">
                            <span className="label">CSV</span>
                            <input className="input jobpayment--input-split-size" maxLength={4} type="text" pattern="[0-9]+" placeholder="XXX" id="cardCode" name="cardCode" value={creditCard.cardCode} onChange={(e) => handleCardCodeChange(e)}></input>
                        </label>
                        <span className="label errors marginLeft20">{errors["cardCode"]}</span>
                    </div>
                </div>
            </div>
            <button className="defaultbutton--save" type="button" onClick={chargeCreditCard}>Charge Card</button>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default JobPaymentData;